// Workflow definitions for the chatbot
// Each workflow contains a name, description, required user inputs, and prompt template

const workflows = [

    {
        name: "Metro Fiber Port Testing MCM",
        description: "Used to determine if any live optical connectivity is present on the panel Metro Fiber"
            + " needs to test. Determines if the MCM is Tier 1 or Tier 3.",
        requiredUserInputs: [
            { name: "MCM_ID", label: "MCM ID", placeholder: "e.g., MCM-139525465" },
            {
                name: "MCM_TITLE",
                label: "MCM Title",
                placeholder: "e.g., FRA053 Urgent Standard Work Testing of"
                    + " Span - 01 - 4 - FRA053 - FRA167 - P01"
            },
            { name: "MCM_DESCRIPTION", label: "MCM Description", placeholder: "Must include panel and port location" }
        ],
        prompt: `{{MCM_ID}} Fiber Port Testing analysis.

-- Begin Instructions

### Task Guidance Instructions
Your job is to locate all passive connections on the panel that Metro Fiber needs to test.
In performing this analysis, apply the principle that FALSE POSITIVES are safer than FALSE NEGATIVES.
It is preferable to surface a potential match to a human rather than ignore a valid match.

To locate the passive connections, you need to locate 1/the target site and 2/the target panel name.
The target site will come from the MCM title provided by the user. The target_site may or may not have a leading zero.  The target panel name from the MCM description. If you cannot determine the target site and target panel name, return control to the user and request them to provide this information.

Once you have the target_site and panel_name, you should execute the follow tool:
tool name: search_links
search_term: "end:/<target_site>-wdm.*/ and linktype:MuxToMux and lifecycle:OPERATIONAL". IMPORTANT - remove any leading zero from the target site you use in the search term. You MUST remove the leading zero from the target_site. For example, if the site name is FRA053, the target_site you will use in all your searches should be FRA53.
filter_fields: [instanceId, lifecycleState, end_a_nsm_name, end_z_nsm_name, link_type, user_provided_id, consumes]

If no links are returned by this search, DO NOT run any more searches. DO NOT broaden the search. DO NOT attempt to locate links in any other manner. DO NOT search by the Z side.
Return the output format to the customer. Say no operational PP exists at this site and the MCM can be safely classified as tier 3.

With any OPERATIONAL MuxToMux links, we must fetch all the links in the collective consumes list. To do this, merge all the consumes list from each individual link into one large list. This list can be called all_operational_passive_links. Then, make the following tool call

tool name: batch_get_links
link_ids: all_operational_passive_links
filter_fields: [instanceId, lifecycleState, end_a_nsm_name, end_z_nsm_name, link_type]

### Fiber Matching Instructions
After you have fetched all the passive links, your final task is to determine if the panel being tested in the MCM is in use by any of the fetched passive to passive links.
To do this, you need to compare the end_a_nsm_name and end_z_nsm_name fields of the passive link objects with the panel_name you parsed earlier.

CRITICAL: You MUST check EVERY single passive link systematically. Do not rely on manual scanning.

For each passive link, perform the following steps:
1. Extract the core panel identifier from the target panel name by removing separators and port information
2. Extract the core panel identifier from both end_a_nsm_name and end_z_nsm_name using the same method
3. Compare the normalized core identifiers

#### Panel Matching Logic:
- Target panel: Extract core components (site, building, rack, RU) from "FRA56‐FTP‐200:2:10:111001:RU39"
- Core identifier would be: "FRA56-FTP-200-2-10-111001-RU39" (normalized)
- For each passive link end name, extract similar core components ignoring:
  * Different separator characters (‐ vs - vs _ vs :)
  * Extra descriptive text ("Data Hall 10", "Ports X-Y")
  * Different formatting of the same location data

#### Fuzzy Matching Logic:
EXACT MATCH: Core panel location data is identical after removing separators and descriptive text
REJECT: Different RU numbers (RU19 vs RU25) or different racks (111001 vs 111025)
ACCEPT AS MATCH: Same location data with only formatting differences

Examples of MATCHING variations for "FRA56‐FTP‐200:2:10:111001:RU39":
- "FRA56-FTP-200:2:10:111001:RU39 Ports 17.18"  ✅
- "FRA56, Data Hall 10, FTP 200:2:10:111001:RU39 13-14"  ✅
- "FRA56_FTP_200 2-10-111001-RU39"  ✅

Examples of NON-MATCHING:
- "FRA56‐FTP‐200:2:10:111025:RU39" ❌ (different rack: 111025 vs 111001)
- "FRA56‐FTP‐201:2:10:111001:RU39" ❌ (different panel: 201 vs 200)
- "FRA56‐FTP‐200:2:10:111001:RU41" ❌ (different RU: 41 vs 39)

#### FTP Fuzzy Matching Logic
FTP stands for Fiber Termination Panel and is a arbitrary number given to a unique panel on a rack. The number is the
important concept. Some fibers may use the term FTP, panel, or only include the raw panel number. Your analysis needs
to account for these differences.

ACCEPT AS MATCH - Same rack location with any of these panel naming variations
For target panel "FTP-001", these should ALL be considered matches:
✅ "FTP-001", "FTP-01", "FTP-1"
✅ "Panel FTP-001", "Panel FTP-01", "Panel FTP-1"
✅ "Panel-1", "Panel 1", "1"
✅ Any variation with same rack + panel number 1

REJECT - Different rack locations regardless of panel naming. Same rack location but different panel number

Key Principle: If the rack location matches exactly, deeper analysis is needed to locate differences in panel naming.
               When in doubt, surface any possible matches. False positives are significantly safer than false negatives.

### Fiber Matching Verification
Verification Required:
Before finalizing your analysis, you MUST:
1. State the total number of passive links retrieved: X links
2. State the total number you identified as matches: Y links
3. For each match, show the original end name and explain why it matched
4. For non-matches, you can summarize but must confirm you checked all X links

### Summary Instructions
Summarize your findings in a clear output. Use emojis and markdown to improve readability. The output format is defined below, follow it exactly:

-- Begin Output Format

### Summary Context
This should contain the MCM ID, parsed target site, and parsed target panel name

### Risk Assessment
For every operational passive links on the panel, reference the following information in the below format
* fiber instanceId (from target passive link)
  * End A (from target passive link end_a_nsm_name)
  * End Z (from target passive link end_z_nsm_name)
  * pp system number (parsed from target passive link parent MuxToMux end name)
  * ots_id (parsed from target passive link parent MuxToMux user_provided_id attribute)


### Reference searches
Include all the search tool calls you to search_links as a part of your analysis.
The way to do this is to take the base URL (\`https://prod.lighthouse.networking.aws.dev/linkservice/linkSearch?searchTerm=\`) and then append the URI encoded version of the search term you provided to search_links.
For example, a search that targeted end:/FRA56-wdm-pp.*/ and linktype:MuxToMux would result in the search URL of \`https://prod.lighthouse.networking.aws.dev/linkservice/linkSearch?searchTerm=end:/FRA56-wdm-pp.*/%20and%20linktype:MuxToMux\`.
To ensure the URL pastes correctly, you need to place any spaces in the search (should only be before and after the "and" keyword with %20. Do not perform any other URI encoding.
The first search you include here should be the first tool use of this session. The second search should be the second search_links tool usage.

IMPORTANT: The searches you include should be the raw https://... URL. Do not format it into a hyperlink. This makes it easier to copy and paste to other tools.

### Recommendation
If you have located operational passive links on the same panel, recommend that the MCM must be tier 1.
If no operational passive links have been located on the same panel, recommend that the MCM can be tier 3.
Keep the recommendation brief to only the tier 1 vs tier 3 classification.

IMPORTANT: If you recommend a tier 3 MCM classification, include the following disclaimer:
This tier 3 determination is based on Lighthouse data. Legacy fiber with non-standard naming conventions may not be detectable, so operational connectivity could exist even if not shown in the data.

-- Begin User Input

MCM ID: {{MCM_ID}}

MCM Title: {{MCM_TITLE}}

MCM Description: {{MCM_DESCRIPTION}}`
    }
];

export default workflows;
